import React, { useEffect, useMemo, useRef, useState } from 'react';
import { PRESERVE_STATE_STREAM_NAMES } from '../../../stream/constants';
import { Provider } from '../../../stream/context';
import create from '../../../stream/create';
import { useSaveStreamStorage } from '../../../stream/streamStorage';
import { disableForm, initFormData, initFormState, resetForm } from '../../../stream/transfer/control';
import apmDispatcher from '../../../utils/apmDispatcher';
import { enhanceFormDescriptor } from '../../../utils/formDescriptor';
import logger from '../../../utils/logger';
import useReduxStore from '../useReduxStore';
const useReferenceChange = (value, valueName, name1, debug)=>{
    const prevValue = useRef(value);
    if (value !== prevValue.current) {
        prevValue.current = value;
        if (debug && typeof value === 'object' && JSON.stringify(Object.keys(value)) === JSON.stringify(Object.keys(prevValue.current))) {
            console.warn(name1, `- ${valueName} has changed, but keys are same. Is this really what you want ? It may indicate unstable reference.`);
        }
        return true;
    }
    return false;
};
const useDebug = (formStream$, { debug = process.env.NODE_ENV === 'development', streamExtension }, formDescriptor)=>{
    useReferenceChange(formDescriptor, 'formDescriptor', name, debug);
    useReferenceChange(streamExtension, 'streamExtension', name, debug);
    useEffect(()=>{
        if (debug) {
            const subscription = logger(formStream$);
            return ()=>{
                subscription.unsubscribe();
            };
        }
    }, [
        formStream$,
        debug
    ]);
};
const useApm = (formStream$, { apm, apmActionTransformer })=>{
    useEffect(()=>{
        if (apm) {
            const subscription = apmDispatcher(formStream$, apmActionTransformer);
            return ()=>{
                subscription.unsubscribe();
            };
        }
    }, [
        formStream$,
        apmActionTransformer,
        apm
    ]);
};
const useInitAndReinitForm = ({ control$ }, { initialFormData, enableReinitialize, disabled }, formDescriptor, nameChanged)=>{
    const initialDataFirstRender = useRef(true);
    if (nameChanged) {
        // name change will completely reset the form
        initialDataFirstRender.current = true;
    }
    const initializedFormData = useMemo(()=>initialFormData instanceof Function ? initialFormData(formDescriptor) : initialFormData, [
        initialFormData,
        formDescriptor
    ]);
    const initializedFormDataChanged = useReferenceChange(initializedFormData);
    useEffect(()=>{
        if (initialDataFirstRender.current) {
            control$.next(initFormData(initializedFormData));
        } else if (!initialDataFirstRender.current && enableReinitialize && initializedFormDataChanged) {
            control$.next(resetForm({
                initialFormData: initializedFormData
            }));
        }
    }, [
        control$,
        initializedFormData,
        enableReinitialize,
        initializedFormDataChanged
    ]);
    useEffect(()=>{
        if (initialDataFirstRender.current) {
            control$.next(initFormState({
                disabled
            }));
        } else if (!initialDataFirstRender.current) {
            control$.next(disableForm(disabled));
        }
    }, [
        control$,
        disabled
    ]);
    useEffect(()=>{
        if (initialDataFirstRender.current) {
            initialDataFirstRender.current = false;
        }
    }, []);
};
const streamCleanUp = (formStream$)=>{
    Object.values(formStream$).forEach((stream$)=>{
        if (stream$.complete) {
            stream$.complete();
        }
    });
    formStream$.closed = true;
};
// warning: react strict mode hell ahead
const useCreateFormStream = ({ name: name1 }, formDescriptor, streamExtension, nameChanged)=>{
    // preserve stream state between stream instances. must have for fast refresh and strict mode.
    const preservedState = useRef({});
    // strict mode in dev mode creates a lot of stuff. thus we need to clean up this mess.
    const leakPurifier = useRef([]);
    // indication of recreating. It cannot be easily distinguished from unmounting.
    const inRemount = useRef(false);
    // it is necessary to force recreate the form stream after "pause" of effects.
    const [forceRecreateCount, setForceRecreate] = useState(1);
    const reduxStore = useReduxStore();
    if (nameChanged) {
        // name change will completely reset the form
        preservedState.current = {};
    }
    const formStream$ = useMemo(()=>{
        const newStream$ = {
            ...create({
                formDescriptor,
                name: name1,
                reduxStore
            }, streamExtension, preservedState.current),
            forceCount: forceRecreateCount,
            uuid: Math.random(),
            closed: false
        };
        // useMemo always runs twice in dev mode. This will add unused instances for cleanup to prevent memory leak.
        // It would be cleaner to add it in render phase, which is possible but it would require another if and find in array.
        // so this is more efficient solution
        leakPurifier.current.push(newStream$);
        return newStream$;
    }, [
        forceRecreateCount,
        formDescriptor,
        name1,
        reduxStore,
        streamExtension
    ]);
    useEffect(()=>{
        // stream is closed only when component is in recreate phase
        // The old instance is already closed and new is not created yet
        if (!formStream$.closed) {
            const subscriptions = PRESERVE_STATE_STREAM_NAMES.map((streamName)=>{
                return formStream$[streamName].subscribe((value)=>{
                    preservedState.current[streamName] = value;
                });
            });
            // cleanup the mess of old unused instances
            leakPurifier.current.filter(({ uuid, closed })=>uuid !== formStream$.uuid && !closed).forEach(streamCleanUp);
            leakPurifier.current = [];
            return ()=>{
                subscriptions.forEach((subscription)=>subscription.unsubscribe());
                streamCleanUp(formStream$);
            };
        }
    }, [
        formStream$
    ]);
    useEffect(()=>{
        // There is no way to detect "true" unmount and mount in strict mode anymore.
        // So this shitty code will force recreate the instance after remount.
        if (inRemount.current) {
            setForceRecreate((i)=>i + 1);
            inRemount.current = false;
        }
        return ()=>{
            inRemount.current = true;
        };
    }, []);
    return formStream$;
};
/**
 * Main hook for form creation
 *
 * @documented
 * @category Form
 * @import import useForm from '@gef-ui/form-core/hooks/form/useForm'
 * @param {Object} input
 * @param {String} input.name
 * @param {Boolean} input.disabled - whole form is disabled
 * @param {Function} input.formDescriptorFn - function for generating of formDescriptor. Currently has no input.
 * @param {Object|Function} input.initialFormData - initial data, can be set only once. Can be either object or a function that will receive initialized descriptors as first param.
 * @param {boolean} [input.enableReinitialize=false] - enable reinitialize of form by changing its initial data. Shallow compare, use memo if you want to avoid reinitialize with same object.
 * @param {boolean} [input.debug=process.env.NODE_ENV === 'development'] - show logger in console when debug on
 * @param {boolean} [input.apm] - dispatch control and transmition actions to APM so breadcrumbs can be created
 * @param {boolean} [input.apmActionTransformer] - function for modifying the final action before it is send to APM
 * @param {Object} [options]
 * @param {Object} [options.streamExtension] - Extension for streams. See source code :)
 * @returns {Object} result
 * @returns {Component} result.FormProvider - context provider for forms
 * @returns {Function} result.formStream$ - bag of all streams
 * @returns {Function} result.formDescriptor - finalized formDescriptor
 * @example
 *
 * 	const { FormProvider } = useForm({name: 'example', formDescriptorFn, initialFormData});
 *
 */ const useForm = (input, streamExtension)=>{
    const { formDescriptorFn } = input;
    const nameChanged = useReferenceChange(input.name);
    const formDescriptor = useMemo(()=>enhanceFormDescriptor(formDescriptorFn()), [
        formDescriptorFn
    ]);
    const formStream$ = useCreateFormStream(input, formDescriptor, streamExtension, nameChanged);
    useSaveStreamStorage(formStream$, input);
    useDebug(formStream$, input, formDescriptor);
    useApm(formStream$, input);
    useInitAndReinitForm(formStream$, input, formDescriptor, nameChanged);
    const FormProvider = ({ children })=>{
        useEffect(()=>{
            console.warn('FormProvider returned from hook has been deprecated. Please see: http://gef.cpas.cz/?path=/story/form-welcome--page#formprovider-deprecation');
        }, []);
        return /*#__PURE__*/ React.createElement(Provider, {
            value: formStream$
        }, children);
    };
    return {
        FormProvider,
        formStream$,
        formDescriptor
    };
};
export default useForm;
